import React, { useEffect, useState } from "react";
import { EditFormDetails, Item } from "../interfaces";
import { GridRowModel } from "@mui/x-data-grid-pro";
import { CircularProgress, InputAdornment, TextField } from "@mui/material";
import { useIntl } from "react-intl";
import { useQuery } from "react-query";
import {
  checkIfAllWatchFieldsValueAreFilled,
  getSelectFilterValues,
  useGetWatchedFieldValue,
} from "../utils";
import { useFormikContext } from "formik";
import { useCreateParams } from "../utils";
import { useDebounce } from "../../hooks/debounce";

/**
 * Autofilled input according other fields input value
 */
const AutoFilledInput = ({
  filter,
  field,
  classes,
  row,
  dynamicPath,
  rows,
}: {
  filter: EditFormDetails;
  field: any;
  classes: any;
  row?: GridRowModel;
  dynamicPath?: boolean;
  rows?: GridRowModel[];
}) => {
  const intl = useIntl();
  const { values, setFieldValue } = useFormikContext<any>();
  const [fieldInputValue, setFieldInputValue] = useState("");
  const watchedFieldValue = useGetWatchedFieldValue(
    values,
    field.name,
    filter.watchField
  );
  const params = useCreateParams(
    filter,
    dynamicPath,
    row,
    rows,
    watchedFieldValue
  );

  const debounceValues = useDebounce(values, 1000);

  /**
   * Fetch data for input
   *
   * Adding "values" in array to refetch query on change. This cause double query but we do this to avoid bug.
   * Indeed, when this input is cleared due to "watchfield" input change, this field is not refilled if user select identical previous data.
   * (e.g : if we look on input data egual to {id: 1, uh_type: {id: 1}} and it change for {id : 2, uh_type: {id: 1}} this will not update this input correctly if we look for uh_type)
   * Moreover, it's little tricky to update this field with useeffect because setFieldValue cause infinit loop (error know by community)
   */
  const query = useQuery(
    [filter.api, params, debounceValues],
    () => getSelectFilterValues<Item>(filter.api as string, params),
    {
      enabled: checkIfAllWatchFieldsValueAreFilled(params, filter.watchField),
    }
  );

  useEffect(() => {
    if (query.data && query.data?.results[0]) {
      const data = query.data?.results[0];
      const value: Record<string, any> = {
        id: data.id,
        label: data[filter.labelKey as string],
      };

      if (filter.keepApiData) {
        filter.keepApiData.forEach((key) => (value[key] = data[key]));
      }

      setFieldValue(filter.key.split(".").join("__"), value);
    }
  }, [
    query.data,
    setFieldValue,
    filter.key,
    filter.labelKey,
    filter.keepApiData,
  ]);

  useEffect(() => {
    if (field.value && field.value.label) {
      setFieldInputValue(field.value.label);
    } else if (query.data) {
      setFieldInputValue(intl.formatMessage({ id: "NO_DATA" }));
    } else {
      setFieldInputValue("");
    }
  }, [field.value, query.data, intl]);

  return (
    <TextField
      {...field}
      className={classes.inputText}
      variant="outlined"
      disabled
      fullWidth
      label={intl.formatMessage({ id: filter.label })}
      // value={field.value?.label || intl.formatMessage({ id: "NO_DATA" })}
      value={fieldInputValue}
      slotProps={{
        input: {
          startAdornment: query.isLoading ? (
            <InputAdornment disablePointerEvents position="start">
              <CircularProgress
                sx={{ height: "20px", width: "20px", color: "text.disabled" }}
                size="small"
              />
            </InputAdornment>
          ) : null,
        },
      }}
    />
  );
};

export default AutoFilledInput;
